import { useEffect, useState } from 'react';
import { api, envPath, type ApiError, type PlayerRow } from '../api';
import { AppealsPanel } from '../components/AppealsPanel';
import { PlayerDrawer } from '../components/PlayerDrawer';
import { ago, Empty, ErrorState, fullDate, PageHeader, shortId, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { useT } from '../i18n';
import { useShell } from './ProjectShell';
import { projectEyebrow } from '../projectNav';

// 入力のたびに検索を投げない（250ms = 打ち終わりを待つが、遅いと感じない境目）
const DEBOUNCE_MS = 250;
const PAGE = 50;

export function PlayersPage() {
  const { project, env } = useShell();
  const base = `${envPath(project.id, env)}/players`;
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [extra, setExtra] = useState<PlayerRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const { t, tr } = useT();

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [q]);

  const first = useResource<{ players: PlayerRow[]; nextCursor: string | null }>(`${base}?limit=${PAGE}${debounced ? `&q=${encodeURIComponent(debounced)}` : ''}`);
  useEffect(() => {
    setExtra([]);
    setCursor(first.data?.nextCursor ?? null);
  }, [first.data]);

  // さらに読み込むの失敗（以前は catch が無く、押しても何も起きないように見えた。dashboard-review 2026-09-27）
  const [moreError, setMoreError] = useState<ApiError | null>(null);
  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const r = await api<{ players: PlayerRow[]; nextCursor: string | null }>(
        `${base}?limit=${PAGE}&cursor=${cursor}${debounced ? `&q=${encodeURIComponent(debounced)}` : ''}`
      );
      setExtra((x) => [...x, ...r.players]);
      setCursor(r.nextCursor);
    } catch (e) {
      setMoreError(e as ApiError);
    } finally {
      setLoadingMore(false);
    }
  };

  const rows = [...(first.data?.players ?? []), ...extra];

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('players.title')} />
      {/* BAN の異議申し立て（受け入れる = BAN が解ける）。BAN を掛ける画面（下のプレイヤーの詳細）の近くに置く = AppealsPanel の WHY */}
      <AppealsPanel base={envPath(project.id, env)} onOpenPlayer={setSelected} onResolved={first.reload} />
      <div className="toolbar">
        <label className="search">
          <span className="sr-only">{t('players.search')}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4.5 4.5" />
          </svg>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('players.searchPlaceholder')} />
        </label>
      </div>

      {first.loading && !first.data && <Spinner />}
      {first.error && <ErrorState error={first.error} what={t('players.title')} onRetry={first.reload} />}
      {first.data && rows.length === 0 && (
        debounced ? (
          <Empty title={t('players.noMatchTitle', { q: debounced })}>
            <p>{t('players.noMatchBody')}</p>
          </Empty>
        ) : (
          <Empty title={t('players.emptyTitle', { env: t(`env.inline.${env}`) })}>
            <p>
              {tr('players.emptyBody', {
                guest: <code>kumo.auth.guest()</code>,
                prefix: <code>{env === 'production' ? 'pk_live_' : 'pk_dev_'}</code>
              })}
            </p>
          </Empty>
        )
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('players.col.player')}</th>
                <th scope="col">{t('players.col.login')}</th>
                <th scope="col" className="hide-sm">{t('players.col.joined')}</th>
                <th scope="col">{t('players.col.lastSeen')}</th>
                <th scope="col">{t('players.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={p.banned ? 'row-banned' : ''}>
                  <td>
                    <button type="button" className="link-btn player-cell" onClick={() => setSelected(p.id)}>
                      <span className="avatar" aria-hidden="true">
                        {(p.displayName ?? '?').slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        <span className="player-name">{p.displayName ?? <em className="muted">{t('common.unnamed')}</em>}</span>
                        <span className="mono muted small">{shortId(p.id)}</span>
                      </span>
                    </button>
                  </td>
                  <td>{p.email ? <span className="small">{p.email}</span> : <span className="pill">{t('players.guestPill')}</span>}</td>
                  <td className="hide-sm" title={fullDate(p.createdAt)}>
                    {ago(p.createdAt)}
                  </td>
                  <td title={fullDate(p.lastSeenAt)}>{ago(p.lastSeenAt)}</td>
                  <td>{p.banned ? <span className="pill pill-danger">{t('players.banned')}</span> : <span className="pill pill-ok">{t('players.active')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cursor && (
        <div className="load-more">
          {moreError && <ErrorState error={moreError} onRetry={() => void loadMore()} />}
          <button type="button" className="btn btn-ghost" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? t('common.loadingMore') : t('common.loadMore')}
          </button>
        </div>
      )}

      <PlayerDrawer
        base={base}
        playerId={selected}
        env={env}
        onClose={() => setSelected(null)}
        onChanged={() => {
          first.reload();
        }}
      />
    </>
  );
}
